import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatName, getInitials } from '@/lib/utils';
import {
  LayoutDashboard, Calendar, Users, ClipboardList, MessageSquare,
  Star, CreditCard, Settings, FileText, Building2, UserCog,
  BarChart3, Bell, Menu, X, LogOut, ChevronLeft, Receipt, Mail
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

const clientNav = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'My Sessions', icon: Calendar, path: '/sessions' },
  { label: 'Questionnaires', icon: ClipboardList, path: '/questionnaires' },
  { label: 'Therapy Progress', icon: FileText, path: '/progress' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
  { label: 'Testimonials', icon: Star, path: '/testimonials' },
  { label: 'Invoices & Receipts', icon: Receipt, path: '/transactions' },
  { label: 'Settings', icon: Settings, path: '/settings' },
];

const therapistNav = [
  { label: 'Calendar', icon: Calendar, path: '/dashboard' },
  { label: 'Patients', icon: Users, path: '/patients' },
  { label: 'Questionnaires', icon: ClipboardList, path: '/questionnaires' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
  { label: 'Transactions & Receipts', icon: CreditCard, path: '/transactions' },
  { label: 'Invoicing', icon: Receipt, path: '/invoicing' },
  { label: 'Testimonials', icon: Star, path: '/testimonials' },
  { label: 'Reports', icon: BarChart3, path: '/reports' },
];

const adminNav = [
  { label: 'Calendar', icon: Calendar, path: '/dashboard' },
  { label: 'Patients', icon: Users, path: '/patients' },
  { label: 'Branches', icon: Building2, path: '/branches' },
  { label: 'Therapists', icon: UserCog, path: '/therapists' },
  { label: 'Questionnaires', icon: ClipboardList, path: '/questionnaires' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
  { label: 'Transactions & Receipts', icon: CreditCard, path: '/transactions' },
  { label: 'Invoicing', icon: Receipt, path: '/invoicing' },
  { label: 'Testimonials', icon: Star, path: '/testimonials' },
  { label: 'Reports', icon: BarChart3, path: '/reports' },
  { label: 'Email Logs', icon: Mail, path: '/email-logs' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const nav = user?.role === 'ADMIN' ? adminNav : user?.role === 'THERAPIST' ? therapistNav : clientNav;

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [nRes, mRes] = await Promise.all([
          api.get('/notifications/unread-count'),
          api.get('/messages/unread-count')
        ]);
        setNotifCount(nRes.data.count);
        setMsgCount(mRes.data.count);
      } catch {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.slice(0, 20));
    } catch {}
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      await api.put(`/notifications/${n._id}/read`);
      setNotifCount(prev => Math.max(0, prev - 1));
    }
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4FAF9]" data-testid="app-layout">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static z-50 h-full bg-white border-r border-[#E5F2F1] flex flex-col transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'} ${mobileOpen ? 'left-0' : '-left-64 lg:left-0'}`} data-testid="sidebar">
        {/* Logo */}
        <div className={`flex items-center justify-center h-16 border-b border-[#E5F2F1] px-3`}>
          <img src="https://mindcareindia.com/wp-content/uploads/2022/01/logo-TMC-NEW.png" alt="Logo" className={`${collapsed ? 'h-8' : 'h-9'} w-auto`} />
        </div>

        {/* Nav */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {nav.map(item => {
              const active = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${active ? 'bg-[#0D8B7D]/10 text-[#0D8B7D] border-l-[3px] border-[#0D8B7D]' : 'text-[#4A6B68] hover:bg-[#F4FAF9] hover:text-[#1A3330]'} ${collapsed ? 'justify-center' : ''}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} />
                  {!collapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                  {!collapsed && item.label === 'Messages' && msgCount > 0 && (
                    <Badge className="ml-auto bg-[#0D8B7D] text-white text-[10px] px-1.5 py-0">{msgCount}</Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Collapse toggle */}
        <div className="p-3 border-t border-[#E5F2F1] hidden lg:block">
          <button onClick={() => setCollapsed(!collapsed)} className="w-full flex items-center justify-center p-2 text-[#4A6B68] hover:text-[#0D8B7D] rounded-lg transition-colors" data-testid="collapse-sidebar-btn">
            <ChevronLeft className={`w-5 h-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E5F2F1] h-16 flex items-center justify-between px-4 lg:px-6" data-testid="topbar">
          <div className="flex items-center gap-5">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-[#4A6B68]" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <img src="https://mindcareindia.com/wp-content/uploads/2022/01/logo-TMC-NEW.png" alt="The Mind Care" className="h-9 w-auto" />
              <p className="text-[11px] italic text-[#0D8B7D] hidden md:block whitespace-nowrap">We Treat Without Medicine</p>
            </div>
            <div className="h-6 w-px bg-[#E5F2F1] hidden sm:block" />
            <h2 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] hidden sm:block whitespace-nowrap">
              {nav.find(n => n.path === location.pathname || (n.path !== '/dashboard' && location.pathname.startsWith(n.path)))?.label || 'Dashboard'}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) fetchNotifications(); }}
                className="relative p-2 rounded-xl text-[#4A6B68] hover:bg-[#F4FAF9] transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {notifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D3544E] text-white text-[10px] rounded-full flex items-center justify-center font-medium">{notifCount > 9 ? '9+' : notifCount}</span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-lg border border-[#E5F2F1] z-50 overflow-hidden" data-testid="notification-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5F2F1]">
                    <span className="font-semibold text-[#1A3330] font-['Outfit'] text-sm">Notifications</span>
                    {notifCount > 0 && <button onClick={markAllRead} className="text-xs text-[#0D8B7D] hover:underline" data-testid="mark-all-read-btn">Mark all read</button>}
                  </div>
                  <ScrollArea className="max-h-80">
                    {notifications.length === 0 && <p className="p-4 text-sm text-[#4A6B68] text-center">No notifications</p>}
                    {notifications.map(n => (
                      <button key={n._id} onClick={() => handleNotifClick(n)} className={`w-full text-left px-4 py-3 border-b border-[#E5F2F1] hover:bg-[#F4FAF9] transition-colors ${!n.is_read ? 'bg-[#0D8B7D]/5' : ''}`}>
                        <p className="text-sm font-medium text-[#1A3330] truncate">{n.title}</p>
                        <p className="text-xs text-[#4A6B68] mt-0.5 truncate">{n.message}</p>
                      </button>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="flex items-center gap-2 pl-2 border-l border-[#E5F2F1]">
              <div className="w-8 h-8 rounded-full bg-[#0D8B7D] flex items-center justify-center text-white text-sm font-medium">
                {getInitials(user)}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-[#1A3330] leading-tight">{formatName(user)}</p>
                <p className="text-[10px] text-[#4A6B68] leading-tight">{user?.role}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#4A6B68] hover:text-[#D3544E]" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
